import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SkuCombobox from "@/components/SkuCombobox";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const newItem = () => ({
  uid: `claim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sku_id: "", quantity: 1,
});

export default function ClaimOrderForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [skus, setSkus] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [form, setForm] = useState({
    existing_order_id: "", customer_name: "", phone: "",
    order_amount: 0, platform: "", order_type_tag: "", notes: "", proof_upload: null,
  });
  const [items, setItems] = useState([newItem()]);
  const [loading, setLoading] = useState(false);
  const [claimedWarning, setClaimedWarning] = useState("");
  const [checking, setChecking] = useState(false);

  const loadSkus = useCallback(async () => {
    try {
      const { data } = await api.get("/skus", { params: { active_only: true } });
      setSkus(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadPlatforms = useCallback(async () => {
    try {
      const { data } = await api.get("/platforms");
      setPlatforms(data);
    } catch { /* not fatal — platforms are optional */ }
  }, []);

  const loadOrderTypes = useCallback(async () => {
    try {
      const { data } = await api.get("/order-types");
      setOrderTypes(Array.isArray(data) ? data : []);
    } catch { /* not fatal */ }
  }, []);

  useEffect(() => { loadSkus(); loadPlatforms(); loadOrderTypes(); }, [loadSkus, loadPlatforms, loadOrderTypes]);

  // Check if the existing_order_id has already been claimed, on blur / debounce
  const checkClaimed = useCallback(async (orderId) => {
    const id = (orderId || "").trim();
    if (!id) { setClaimedWarning(""); return; }
    setChecking(true);
    try {
      const { data } = await api.get("/claims/check", { params: { existing_order_id: id } });
      if (data.already_claimed) {
        setClaimedWarning(
          `This order ID is already claimed (status: ${data.status}). It can't be claimed again.`
        );
      } else {
        setClaimedWarning("");
      }
    } catch { setClaimedWarning(""); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    const id = form.existing_order_id;
    const t = setTimeout(() => checkClaimed(id), 500);
    return () => clearTimeout(t);
  }, [form.existing_order_id, checkClaimed]);

  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    setItems(next);
  };
  const addItem = () => setItems([...items, newItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, proof_upload: data.path });
      toast.success("Proof uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (claimedWarning) {
      toast.error(claimedWarning);
      return;
    }
    if (!form.order_type_tag) { toast.error("Select an Order Type"); return; }
    // Only send valid item rows
    const validItems = items
      .filter((it) => it.sku_id && Number(it.quantity) > 0)
      .map((it) => ({ sku_id: it.sku_id, quantity: Number(it.quantity) }));
    setLoading(true);
    try {
      await api.post("/claims", {
        ...form,
        order_amount: Number(form.order_amount),
        platform: form.platform,
        items: validItems.length > 0 ? validItems : undefined,
      });
      toast.success("Claim submitted");
      navigate("/claims");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Submit Claim Order</h1>
        <p className="text-sm text-muted-foreground">Claim an existing order. Approval team will review.</p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <Card className="p-4 md:p-6 space-y-4">
          <div className="space-y-2">
            <Label>BDM name</Label>
            <Input value={user.name} disabled data-testid="claim-agent-name" />
          </div>
          <div className="space-y-2">
            <Label>Existing order ID</Label>
            <Input
              required
              value={form.existing_order_id}
              onChange={(e) => setForm({ ...form, existing_order_id: e.target.value })}
              data-testid="claim-order-id"
              className={claimedWarning ? "border-red-500 focus-visible:ring-red-500" : ""}
            />
            {checking && <p className="text-xs text-muted-foreground">Checking…</p>}
            {claimedWarning && (
              <p className="text-xs text-red-600" data-testid="claim-already-claimed-warning">
                {claimedWarning}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer name</Label>
              <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="claim-customer" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="claim-phone" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Order amount (₹)</Label>
              <Input type="number" step="0.01" required value={form.order_amount} onChange={(e) => setForm({ ...form, order_amount: e.target.value })} data-testid="claim-amount" />
            </div>
            <div className="space-y-2">
              <Label>Platform {platforms.length === 0 && <span className="text-xs text-muted-foreground">(admin hasn&apos;t added any yet)</span>}</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })} disabled={platforms.length === 0}>
                <SelectTrigger data-testid="claim-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  {platforms.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Type * {orderTypes.length === 0 && <span className="text-xs text-muted-foreground">(admin hasn&apos;t added any yet)</span>}</Label>
              <Select value={form.order_type_tag} onValueChange={(v) => setForm({ ...form, order_type_tag: v })} disabled={orderTypes.length === 0}>
                <SelectTrigger data-testid="claim-order-type-tag"><SelectValue placeholder="Select order type..." /></SelectTrigger>
                <SelectContent>
                  {orderTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display font-semibold text-lg">Products in the order</h2>
              <p className="text-sm text-muted-foreground mt-1">Select each SKU and its quantity. No price needed — this is informational for the approval team.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="claim-add-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={it.uid} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border-b border-border pb-3 last:border-0" data-testid={`claim-item-${i}`}>
                <div className="md:col-span-8 space-y-2">
                  <Label>SKU</Label>
                  <SkuCombobox
                    skus={skus}
                    value={it.sku_id}
                    onChange={(v) => updateItem(i, "sku_id", v)}
                    testid={`claim-item-sku-${i}`}
                  />
                </div>
                <div className="md:col-span-3 space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number" min="1"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    data-testid={`claim-item-qty-${i}`}
                  />
                </div>
                <div className="md:col-span-1 text-right">
                  {items.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} data-testid={`claim-item-remove-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <div className="space-y-2">
            <Label>Proof / screenshot (optional)</Label>
            <Input type="file" accept="image/*" onChange={onUpload} data-testid="claim-proof-input" />
            {form.proof_upload && <span className="text-xs text-green-700">Uploaded</span>}
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="claim-notes" />
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading || !!claimedWarning} data-testid="submit-claim-btn">{loading ? "Submitting..." : "Submit Claim"}</Button>
        </div>
      </form>
    </div>
  );
}
